/**
 * Step 4 of the bulk upload: the review. Rows the Items API rejected are listed first, with an
 * editor per field (`BulkUploadHelpers.tsx` → `buildRows`) and "Process Corrections & Try Again";
 * the rows ready to add follow, read-only. Either table's trash icon drops a row.
 *
 * Correction loop: edits collect in `invalidDataRef`. The button copies them into `invalidData`
 * and, when a row was edited or has a quantity error, sets `processCorrections`, which makes
 * `BulkUploadModal.tsx` re-validate the rows awaiting correction. A row that comes back valid stays
 * in the corrections table, marked resolved, for 3 s and then joins the ready rows.
 *
 * On mount, the corrections table looks up each rejected row's part number
 * (`GET APIUrl.SEARCH_PRODUCTS`, first result) so the UOM dropdowns can offer that product's units.
 *
 * Test: `unit-tests/shared/BulkUploadModal/UploadValidation.spec.tsx`.
 */
import React, { useEffect, useState } from "react";
import { useApplication } from "@ryerson/frontend.application";
import { APIUrl } from "@enums/apiUrl.enums";
import { UploadValidationContent, UploadValidationText } from "./UploadValidationContent";
import { buildRows, removeRow } from "./BulkUploadHelpers";
import { Typography } from "@ryerson/frontend.typography";
import { Flex } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@ryerson/frontend.table";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { IndexedLayers, Product } from "@ryerson/frontend.common";
import { Button } from "@ryerson/frontend.button";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";
import { BulkUploadType } from "./BulkUpload.model";

import { PartValidationRes } from "./BulkUpload.model";

type UploadValidationProps = {
	validatedData: PartValidationRes[];
	setValidatedData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>;
	processCorrections: boolean;
	setProcessCorrections: React.Dispatch<React.SetStateAction<boolean>>;
	invalidData: PartValidationRes[];
	setInvalidData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>;
	invalidDataRef: React.MutableRefObject<PartValidationRes[]>;
	bulkUploadType: BulkUploadType;
};

type ValidationTableRows = {
	[k: string]: JSX.Element;
}[];

/**
 * Column keys of the two review tables, matching `UploadValidationContent.ts` → `headers`. The
 * ready table drops `errorMessage`; the corrections table drops `confirmation`.
 */
export enum HeaderKeys {
	partNumber = "partNumber",
	quantity = "quantity",
	quantityUom = "quantityUom",
	pricingUom = "pricingUom",
	confirmation = "confirmation",
	errorMessage = "errorMessage",
	trash = "trash",
}

type ValidationTableHeaders = { [key in keyof typeof HeaderKeys]: string };

type ValidRowsTableProps = {
	headers: ValidationTableHeaders;
	content: UploadValidationText;
	rows?: ValidationTableRows;
};

type InValidRowsTableProps = ValidRowsTableProps & {
	validatedData: PartValidationRes[];
	setValidatedData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>;
	invalidData: PartValidationRes[];
	setInvalidData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>;
	invalidDataRef: React.MutableRefObject<PartValidationRes[]>;
};

/** Step 4: the review of the validated rows; see the file header. */
export const UploadValidation: React.FC<UploadValidationProps> = ({
	validatedData,
	setValidatedData,
	invalidData,
	setInvalidData,
	setProcessCorrections,
	invalidDataRef,
	bulkUploadType,
}) => {
	const { theme } = useTheme();
	const { header: headerColor, selected, error } = theme.colors.primary;

	const {
		localization: { language },
	} = useApplication();

	const content = UploadValidationContent[language || "en"];

	const { headers } = content;

	const validRowsHeaders: ValidationTableHeaders = Object.fromEntries(
		Object.entries(headers).filter(([key, value]) => key !== "errorMessage")
	) as ValidationTableHeaders;

	const invalidRowsHeaders: ValidationTableHeaders = Object.fromEntries(
		Object.entries(headers).filter(([key, value]) => key !== "confirmation")
	) as ValidationTableHeaders;

	const invalidRowsNumber = validatedData.filter((dataRow) => !dataRow.valid).length;

	const [validRows, setValidRows] = useState<ValidationTableRows>(
		validatedData
			.filter((data) => data.valid)
			.map((row) =>
				Object.fromEntries(
					Object.entries(row).map(([key, value], index) => [
						key,
						<Flex key={index}>
							<Typography variant={"div"}>{value}</Typography>
						</Flex>,
					])
				)
			)
	);

	useEffect(() => {
		setValidRows(
			[...validatedData]
				.filter((data) => data.valid)
				.map((row) =>
					Object.fromEntries(
						Object.entries(row)
							.map(([key, value], index) => [
								key,
								<Typography key={index}>
									{/* For a valid row the API sets partNumber to the customer
									    part number or "", so the SKU stands in. */}
									{key === "id"
										? parseInt(value) + 1
										: key === "partNumber"
											? value || row.ryersonSku
											: value}
								</Typography>,
							])
							.concat([
								[
									"trash",
									<Icon
										size="sm"
										onClick={() => {
											removeRow(row, validatedData, setValidatedData);
										}}
										icon={"trash"}
										key={Object.keys(row).length + 1}
									/>,
								],
							])
					)
				)
		);
	}, [validatedData]);

	// Once re-validation returns a valid row, merge `invalidData` into `validatedData` after 3 s;
	// `BulkUploadModal.tsx` then drops the now-valid rows from the corrections list. Until then the
	// row shows as resolved in the corrections table.
	useEffect(() => {
		if (invalidData.find((rowData) => rowData.valid)) {
			setTimeout(() => {
				setValidatedData(
					[...validatedData].map(
						(rowData) => invalidData.find((entry) => entry.id === rowData.id) ?? rowData
					)
				);
			}, 3000);
		}
	}, [invalidData]);

	/**
	 * "Process Corrections & Try Again". Rows are re-sent only when one was edited or has a
	 * `quantity` error. One such error is the Items API's "Quantity has been updated": the row
	 * already carries the quantity the API changed to a whole number of pieces, so it can pass
	 * unchanged.
	 */
	const handleProcessCorrectionsClick = () => {
		const newInvalidData = [...invalidDataRef.current];
		setInvalidData(newInvalidData);

		if (newInvalidData.find((row) => row.updatedByUser || row.errorField === "quantity")) {
			setProcessCorrections(true);
			let updatedValidatedData = [...validatedData].map(
				(entry) => newInvalidData.find((data) => data.id === entry.id) ?? entry
			);
			setValidatedData(updatedValidatedData);
		}
	};

	return (
		<>
			{invalidDataRef.current?.length > 0 && (
				<>
					<Typography color={error} css={{ marginBottom: 10, marginTop: 20 }}>
						{content.unableToProcess.replace(/%[^%]*%/, invalidRowsNumber.toString()) +
							(invalidRowsNumber > 1 ? "s" : "") +
							content.pleaseReview}
					</Typography>
					<InvalidRowsTable
						headers={invalidRowsHeaders}
						content={content}
						validatedData={validatedData}
						setValidatedData={setValidatedData}
						invalidData={invalidData}
						setInvalidData={setInvalidData}
						invalidDataRef={invalidDataRef}
					/>
					<Button
						size="lg"
						css={{ marginTop: 20 }}
						shape="rounded"
						label={content.processCorrections}
						foreground="light"
						background={selected}
						onClick={handleProcessCorrectionsClick}
						dataTestId="bulk-upload-modal-process-correction-button"
					/>
				</>
			)}
			<Flex css={{ marginTop: 65 }}>
				<Typography weight="bold" color={headerColor} css={{ marginBottom: 20 }}>
					{content.itemsAreReady
						.replace(/%readyItems%/, validRows.length.toString())
						.replace(
							/%itemsAre%/,
							validRows.length > 1 ? content.itemsAre : content.itemIs
						)
						.replace(
							/%ShoppingCart%/,
							bulkUploadType === BulkUploadType.shoppingCart
								? content.shoppingCart
								: bulkUploadType === BulkUploadType.quote
									? content.quote
									: content.shoppingList
						)}
				</Typography>
			</Flex>
			<ValidRowsTable headers={validRowsHeaders} content={content} rows={validRows} />
		</>
	);
};

/** The corrections table: editable cells from `buildRows`, numbered by file row. */
const InvalidRowsTable: React.FC<InValidRowsTableProps> = ({
	headers,
	content,
	validatedData,
	setValidatedData,
	invalidData,
	setInvalidData,
	invalidDataRef,
}) => {
	const { theme } = useTheme();
	const {
		axiosInstance,
		zipCode,
		user: { isLoggedIn },
		localization: { language },
	} = useApplication();
	const { selectedShipToAddress, customerPickup, salesPlant } = useEcommerce();
	const { lighterGray, gray, header: headerColor, success } = theme.colors.primary;

	const getProductDetails = async (partNumber: string): Promise<Product | null> => {
		let inputData = encodeURIComponent(partNumber);
		let queryParams = `search-phrase=${inputData}`;

		queryParams += `&zip-code=${getZipCode(
			isLoggedIn,
			zipCode,
			selectedShipToAddress,
			customerPickup,
			salesPlant
		)}`;

		const response = await axiosInstance.get(`${APIUrl.SEARCH_PRODUCTS}?${queryParams}`);

		let responseData = response?.data;
		return responseData?.length > 0 ? responseData[0] : null;
	};

	// Runs once per mount: one search per rejected row, in parallel. There is no failure handler;
	// if any search fails, no row gets product details and the UOM dropdowns list every unit.
	useEffect(() => {
		const promises = invalidData.map((rowData) => getProductDetails(rowData.partNumber));
		Promise.all(promises).then((products) => {
			const updatedData = [...validatedData];
			products.forEach((product, index) => {
				if (product) {
					const rowIndex = updatedData.findIndex(
						(cur) => cur.id === invalidData[index].id
					);
					updatedData[rowIndex] = {
						...updatedData[rowIndex],
						productDetails: product,
					};
				}
			});
			setValidatedData(updatedData);
		});
	}, []);

	return (
		<>
			<Table
				addStyleString={`
						width: 100%;
						border-collapse: collapse;
						border-spacing: 0px;
						position: relative;
						overflow-y: auto;
						overflow-x: unset;
					max-height: 300px;
					border: solid ${lighterGray} 1px;
					`}
			>
				<TableHead
					css={css`
						display: table;
						width: 100%;
						table-layout: fixed;
						top: 0;
						position: sticky;
						z-index: ${IndexedLayers.base};
					`}
				>
					<TableRow
						css={css`
							background: ${lighterGray};
							height: 60px;
						`}
					>
						{Object.entries(headers)?.map(([headerKey, headerValue], index) => (
							<TableCell
								key={index}
								css={css`
									padding: 0 0 0 20px;
									${headerKey === "trash" && "width: 60px; padding-right: 20px;"}
									${headerKey === HeaderKeys.partNumber && "width: 300px;"}
								`}
							>
								{
									<Flex alignItems="flex-end" justifyContent="flex-start">
										<Typography size="sm" color={headerColor} weight="bold">
											{headerValue}
										</Typography>
										{headerValue === headers.pricingUom && (
											<Typography
												size="sm"
												color={gray}
												css={{ marginLeft: 5 }}
											>
												{content.optional}
											</Typography>
										)}
									</Flex>
								}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					<>
						{buildRows(
							validatedData,
							setValidatedData,
							invalidData,
							invalidDataRef,
							theme,
							language
						)?.map((cells, index) => (
							<TableRow
								key={index}
								css={css`
									display: table;
									width: 100%;
									height: 60px;
									table-layout: fixed;
								`}
							>
								{Object.keys(headers)?.map((headerKey, index) => (
									<TableCell
										key={index}
										css={css`
											padding: 0 0 0 20px;
											${headerKey === "trash" &&
											"width: 60px; padding-right: 20px;"}
											${headerKey === HeaderKeys.partNumber &&
											"width: 300px;"}
										`}
									>
										<Flex alignItems="center">
											<>
												{headerKey === HeaderKeys.partNumber && (
													<Typography
														weight="bold"
														variant="div"
														color="header"
													>
														{cells["id"]}.&nbsp;
													</Typography>
												)}
												{cells[headerKey] ?? (
													<Typography size="sm" color={success}>
														{content.errorResolved}
													</Typography>
												)}
											</>
										</Flex>
									</TableCell>
								))}
							</TableRow>
						))}
					</>
				</TableBody>
			</Table>
		</>
	);
};

/** The read-only table of rows ready to add, each confirmed "OK". */
const ValidRowsTable: React.FC<ValidRowsTableProps> = ({ headers, content, rows }) => {
	const { theme } = useTheme();
	const { lighterGray, gray, header: headerColor, success } = theme.colors.primary;

	return (
		<>
			<Table
				addStyleString={` 
					width: 100%;
					border-collapse: collapse;
					border-spacing: 0px;
					position: relative;
					overflow-y: auto;
					overflow-x: unset;
					max-height: 300px;
					border: solid ${lighterGray} 1px;
					margin-bottom: 20px;
					`}
			>
				<TableHead
					css={css`
						display: table;
						width: 100%;
						table-layout: fixed;
						top: 0;
						position: sticky;
						z-index: ${IndexedLayers.base};
					`}
				>
					<TableRow
						css={css`
							display: table;
							width: 100%;
							height: 60px;
							table-layout: fixed;
						`}
					>
						{Object.entries(headers)?.map(([headerKey, header], index) => (
							<TableCell
								key={index}
								css={css`
									padding: 0 0 0 20px;
									${headerKey === "trash" && "width: 60px; padding-right: 20px;"}
								`}
							>
								{
									<Flex alignItems="flex-end" justifyContent="flex-start">
										<Typography size="sm" color={headerColor} weight="bold">
											{header}
										</Typography>
										{header === headers.pricingUom && (
											<Typography
												size="sm"
												color={gray}
												css={{ marginLeft: 5 }}
											>
												{content.optional}
											</Typography>
										)}
									</Flex>
								}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					<>
						{rows?.map((cells, index) => (
							<TableRow
								key={index}
								css={css`
									display: table;
									width: 100%;
									height: 60px;
									table-layout: fixed;
								`}
							>
								{Object.keys(headers)?.map((headerKey, index) => (
									<TableCell
										key={index}
										css={css`
											padding: 0 0 0 20px;
											${headerKey === "trash" &&
											"width: 60px; padding-right: 20px;"}
										`}
									>
										{headerKey === HeaderKeys.confirmation ? (
											<Typography size="sm" color={success}>
												{content.OK}
											</Typography>
										) : (
											<Flex alignItems="center">
												{headerKey === HeaderKeys.partNumber && (
													<Typography weight="bold" color="header">
														{cells["id"]}.&nbsp;
													</Typography>
												)}
												<Typography size="sm">
													{cells[headerKey]}
												</Typography>
											</Flex>
										)}
									</TableCell>
								))}
							</TableRow>
						))}
					</>
				</TableBody>
			</Table>
		</>
	);
};
